/**
 * Utilidades genéricas para exportar datos tabulares a CSV y descargarlos como
 * archivo local — sin subir a ningún servicio externo. Reutilizado por
 * CobrosView (cobros/pagos) y TherapistCalendar (agenda de consultas).
 *
 * Mismo criterio que icsGenerator.ts: blob local + <a download>, con fallback
 * data: URI en iOS Safari (WebKit no dispara la descarga de blob: con el
 * atributo `download` en navegadores in-app / iOS).
 */

export type ExportRangeKind = 'semana' | 'mes' | 'mes_anterior' | 'personalizado';

export interface ExportRange {
  kind: ExportRangeKind;
  desde: string; // YYYY-MM-DD
  hasta: string; // YYYY-MM-DD
  label: string; // rótulo legible, usado en UI y nombre de archivo
}

const pad2 = (n: number) => String(n).padStart(2, '0');
const toISODate = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/**
 * Resuelve un rango de fechas a partir de un atajo (semana/mes actual, mes
 * anterior) o un rango personalizado ya validado por el caller.
 * `todayStr` debe venir anclado a America/Santiago (ver toSantiagoDateStr en
 * dateUtils.ts) para no depender del huso horario de la máquina del cliente.
 */
export function resolveExportRange(
  kind: ExportRangeKind,
  todayStr: string,
  custom?: { desde: string; hasta: string }
): ExportRange {
  const [y, m, d] = todayStr.split('-').map(Number);
  const today = new Date(y, m - 1, d);

  if (kind === 'semana') {
    // Lunes a domingo de la semana que contiene "hoy"
    const dow = today.getDay(); // 0 = domingo
    const diffToMonday = dow === 0 ? -6 : 1 - dow;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diffToMonday);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { kind, desde: toISODate(monday), hasta: toISODate(sunday), label: 'semana actual' };
  }

  if (kind === 'mes') {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    const last = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return { kind, desde: toISODate(first), hasta: toISODate(last), label: 'mes actual' };
  }

  if (kind === 'mes_anterior') {
    const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const last = new Date(today.getFullYear(), today.getMonth(), 0);
    return { kind, desde: toISODate(first), hasta: toISODate(last), label: 'mes anterior' };
  }

  // personalizado
  if (!custom?.desde || !custom?.hasta) {
    throw new Error('Rango personalizado requiere fecha desde y hasta.');
  }
  return { kind, desde: custom.desde, hasta: custom.hasta, label: 'rango personalizado' };
}

/** Escapa un valor para CSV (RFC 4180). */
function escapeCSVField(value: string | number | null | undefined): string {
  if (value == null) return '';
  const str = String(value);
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Construye el contenido CSV (con BOM UTF-8 para que Excel en Windows
 * reconozca acentos/ñ sin configurar encoding manual).
 */
export function buildCSVContent(
  headers: string[],
  rows: (string | number | null | undefined)[][]
): string {
  const BOM = '﻿';
  const lines = [
    headers.map(escapeCSVField).join(','),
    ...rows.map(row => row.map(escapeCSVField).join(',')),
  ];
  return BOM + lines.join('\r\n');
}

/**
 * Descarga un array de filas como CSV local (blob + <a download>).
 */
export function downloadCSV(
  filename: string,
  headers: string[],
  rows: (string | number | null | undefined)[][]
): void {
  const content = buildCSVContent(headers, rows);

  // iOS Safari (y navegadores in-app tipo WKWebView) no soportan de forma
  // confiable `blob:` + `<a download>` — mismo motivo que downloadICSEvent en
  // icsGenerator.ts. Navegar a un data: URI sí dispara la descarga en iOS.
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

  if (isIOS) {
    window.location.href = `data:text/csv;charset=utf-8,${encodeURIComponent(content)}`;
    return;
  }

  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
